/**
 * A Spinner component that will report REPORT_PERCENT percent of timeouts to
 * Sentry and optionally display apologetic messages to the user and ask them to
 * refresh. This component uses the WonderBlocks spinner.
 */

import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import type {Metadata} from "@khanacademy/wonder-stuff-core";

type DefaultProps = {
    // WonderBlocks Spinner Props
    /**
     * The size of the spinner. (large = 96px, medium = 48px, small = 24px,
     * xsmall = 16px)
     */
    size: "xsmall" | "small" | "medium" | "large";
    /**
     * Should a light version of the spinner (and optional notification text)
     * be shown?
     * (To be used on a dark background.)
     */
    light: boolean;
    /**
     * Number of seconds after which we should report a timeout error to Sentry
     * and (optionally) alert the user if `notifyUser` is set.
     *
     * Default: 15 seconds
     */
    timeoutSeconds: number;
};

type Props = DefaultProps & {
    // Props specific to TimeoutSpinner
    /** A unique ID for identifying timeouts in Sentry */
    sentryId: string;
    /** A short description of what this spinner is waiting on */
    description: string;
    /** Any additional info to be logged to Sentry when reporting a timeout */
    sentryExtras?: Metadata;
    /**
     * (optional) Display an apologetic message if the timeout is hit. If the
     * spinner is still displaying after 2 * timeoutSeconds, the user is
     * instructed to refresh the page.
     */
    notifyUser?: boolean;
    /**
     * Any (optional) styling to apply to the spinner
     */
    style?: StyleType;
};

/**
 * @deprecated Use CircularSpinner from @khanacademy/wonder-blocks-progress-spinner.
 */
export default class DeprecatedTimeoutSpinner extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        size: "large",
        light: false,
        timeoutSeconds: 15,
    };

    render(): React.ReactNode {
        const {size, light, style} = this.props;

        return (
            <View style={[styles.containerDefault, style]}>
                <CircularSpinner size={size} light={light} />
            </View>
        );
    }
}

const styles = StyleSheet.create({
    containerDefault: {
        alignItems: "center",
    },
});
